import {
  CalendarDaysIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardListIcon,
  FolderOpenIcon,
  Grid3x3Icon,
  LayoutGridIcon,
  MoreVerticalIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react'
import { Pagina } from '@/components/Pagina'
import { Boton } from '@/components/ui/Boton'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
  MenuSeparador,
} from '@/components/ui/MenuDesplegable'
import { cn } from '@/lib/cn'
import type { FiltroDestino } from '@/lib/schemas/publicaciones'
import { correrMes, formatearMes } from './formato'
import { COLUMNAS_POSIBLES, type Columnas } from './medidas'

interface HerramientasProps {
  mes: string
  alCambiarMes: (mes: string) => void
  /**
   * Sólo en la vista Grilla, para todos: la grilla entera, sin corte por mes.
   * Mientras está puesta, el mes se reemplaza por un chip con su ×.
   */
  completa?: boolean
  alCambiarCompleta?: (v: boolean) => void
  /**
   * Todo · Feed · Historias (DEC-21), en grilla y calendario, para todos. Va
   * a la vista y no en un menú: puesto, se nota (OBS-017).
   */
  filtroDestino?: { valor: FiltroDestino; alCambiar: (d: FiltroDestino) => void }
  /** Sólo RASTRO: el cliente revisa siempre la vista completa del mes. */
  filtrosRastro?: {
    pilares: { id: string; nombre: string; color: number }[]
    pilarIds: string[]
    alCambiarPilares: (ids: string[]) => void
    mostrarDeBaja: boolean
    alCambiarDeBaja: (v: boolean) => void
    /** Saca todos los filtros de una vez (pilares y dadas de baja). */
    alQuitarTodos: () => void
  }
  /**
   * Acciones de RASTRO dentro del menú ⋮ en celular. En escritorio van en la
   * barra de arriba (EncabezadoProyecto).
   */
  accionesMenu?: React.ReactNode
  /**
   * Columnas de la grilla, sólo en escritorio (DEC-08). La elige cada persona
   * y la recuerda su computadora.
   */
  columnas?: { valor: Columnas; alCambiar: (n: Columnas) => void }
  /** Vista activa del conmutador (§5.1; Planificación y Recursos desde las Fases 5a y 5b). */
  vista: Vista
  alCambiarVista: (v: Vista) => void
}

export type Vista = 'planificacion' | 'grilla' | 'calendario' | 'recursos'

/** El orden de las pestañas: Planificación · Grilla · Calendario · Recursos. */
export const VISTAS: { valor: Vista; etiqueta: string; icono: LucideIcon }[] = [
  { valor: 'planificacion', etiqueta: 'Planificación', icono: ClipboardListIcon },
  { valor: 'grilla', etiqueta: 'Grilla', icono: Grid3x3Icon },
  { valor: 'calendario', etiqueta: 'Calendario', icono: CalendarDaysIcon },
  { valor: 'recursos', etiqueta: 'Recursos', icono: FolderOpenIcon },
]

/**
 * Fila de herramientas del §5.1, simplificada con la revisión del 21/09:
 * conmutador de vista y mes, nada más a la vista. La grilla imita el perfil
 * de Instagram, así que el orden por fecha se entiende solo.
 *
 * "Ver grilla completa" (antes "Todos los meses", que se confundía con un
 * filtro de mes) muestra el feed entero. Queda a la vista en escritorio y en
 * el ⋮ en celular; puesta, el mes cede su lugar a un chip con ×.
 *
 * El filtro de pilares y "Mostrar dadas de baja" son de RASTRO y viven en el
 * menú ⋮: el cliente no filtra, porque revisar sobre una vista filtrada es
 * revisar a medias. Cuando hay un filtro puesto se ve como chip, con su ×,
 * para que nadie trabaje sobre una vista parcial sin saberlo.
 *
 * Todo · Feed · Historias (DEC-21) es la excepción: lo pidió Serena para
 * todos, en grilla y calendario. Las dos vistas abren con Todo, así lo que
 * espera revisión siempre está a la vista; el filtro se elige a la vista y,
 * puesto, se ve como chip de color (OBS-017).
 *
 * En celular la fila ocupa una sola línea: mes y ⋮. Las pestañas van en
 * una barra fija abajo (`BarraVistas`), porque cuatro no entran arriba.
 * Planificación y Recursos no tienen mes ni filtros: en celular la fila
 * desaparece y en escritorio quedan sólo las pestañas.
 */
export function Herramientas({
  mes,
  alCambiarMes,
  completa = false,
  alCambiarCompleta,
  filtroDestino,
  filtrosRastro,
  accionesMenu,
  columnas,
  vista,
  alCambiarVista,
}: HerramientasProps) {
  const f = filtrosRastro
  const alternarPilar = (id: string) => {
    if (!f) return
    f.alCambiarPilares(
      f.pilarIds.includes(id) ? f.pilarIds.filter((x) => x !== id) : [...f.pilarIds, id],
    )
  }
  const pilaresActivos = f ? f.pilares.filter((p) => f.pilarIds.includes(p.id)) : []
  const verBaja = Boolean(f?.mostrarDeBaja) && vista === 'grilla'
  const puedeCompleta = vista === 'grilla' && Boolean(alCambiarCompleta)
  const conMes = vista === 'grilla' || vista === 'calendario'
  const destino = conMes && filtroDestino ? filtroDestino.valor : 'todo'
  const hayChips = pilaresActivos.length > 0 || verBaja || destino !== 'todo'
  const cantidadChips =
    pilaresActivos.length + (verBaja ? 1 : 0) + (destino !== 'todo' ? 1 : 0)
  const hayMenu = conMes && (Boolean(f) || puedeCompleta)

  return (
    <div
      className={cn('border-b border-borde bg-lienzo/80', !conMes && 'hidden sm:block')}
    >
      {/* Envuelve en vez de encimarse: con "Enviar N borradores a revisión"
          al lado, "Ver grilla completa" quedaba tapado (Serena, 22/09). */}
      <Pagina className="flex flex-wrap items-center gap-2 py-2.5">
        {/* En celular las pestañas van en la barra de abajo. */}
        <div
          className="hidden shrink-0 rounded-full bg-tarjeta p-1 ring-1 ring-borde sm:flex"
          role="tablist"
          aria-label="Vista"
        >
          {VISTAS.map((v) => (
            <button
              key={v.valor}
              type="button"
              role="tab"
              aria-selected={vista === v.valor}
              onClick={() => {
                alCambiarVista(v.valor)
              }}
              className={cn(
                'rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors min-[1500px]:px-4',
                // Las pestañas también reaccionan al mouse (DEC-23, OBS-018).
                vista === v.valor
                  ? 'bg-tinta text-white'
                  : 'text-tinta-suave hover:bg-lienzo hover:text-tinta',
              )}
            >
              {v.etiqueta}
            </button>
          ))}
        </div>

        {!conMes ? null : completa && puedeCompleta ? (
          <span
            data-grilla-completa
            className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-tarjeta py-1 pr-1 pl-3 text-[13px] font-medium ring-1 ring-borde"
          >
            <span className="truncate">Grilla completa</span>
            <button
              type="button"
              aria-label="Volver a ver por mes"
              onClick={() => {
                alCambiarCompleta?.(false)
              }}
              className="flex size-7 shrink-0 items-center justify-center rounded-full hover:bg-lienzo"
            >
              <XIcon className="size-3.5" aria-hidden />
            </button>
          </span>
        ) : (
          <div className="flex min-w-0 items-center">
            <Boton
              variante="sutil"
              tamano="icono"
              aria-label="Mes anterior"
              onClick={() => {
                alCambiarMes(correrMes(mes, -1))
              }}
            >
              <ChevronLeftIcon className="size-5" aria-hidden />
            </Boton>
            <span
              data-mes
              className="min-w-0 truncate text-center text-sm font-medium sm:min-w-32"
            >
              {formatearMes(mes)}
            </span>
            <Boton
              variante="sutil"
              tamano="icono"
              aria-label="Mes siguiente"
              onClick={() => {
                alCambiarMes(correrMes(mes, 1))
              }}
            >
              <ChevronRightIcon className="size-5" aria-hidden />
            </Boton>
            {puedeCompleta && (
              <Boton
                variante="sutil"
                tamano="chico"
                className="hidden shrink-0 whitespace-nowrap sm:inline-flex"
                onClick={() => {
                  alCambiarCompleta?.(true)
                }}
              >
                <LayoutGridIcon className="size-4" aria-hidden />
                Ver grilla completa
              </Boton>
            )}
          </div>
        )}

        {conMes && filtroDestino && (
          // En celular baja a su propio renglón, a lo ancho: no entra junto al mes.
          <div
            role="radiogroup"
            aria-label="Qué publicaciones ver"
            data-filtro-destino={destino}
            className="order-last flex w-full items-center rounded-full bg-tarjeta p-1 text-[13px] ring-1 ring-borde sm:order-none sm:w-auto"
          >
            {OPCIONES_DESTINO.map((o) => (
              <button
                key={o.valor}
                type="button"
                role="radio"
                aria-checked={destino === o.valor}
                onClick={() => {
                  filtroDestino.alCambiar(o.valor)
                }}
                className={cn(
                  'flex-1 rounded-full px-2.5 py-1.5 font-medium whitespace-nowrap sm:flex-none',
                  destino === o.valor
                    ? 'bg-tinta text-white'
                    : 'text-tinta-suave hover:bg-lienzo',
                )}
              >
                {o.etiqueta}
              </button>
            ))}
          </div>
        )}

        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
          {columnas && vista === 'grilla' && (
            <div
              role="radiogroup"
              aria-label="Columnas de la grilla"
              title="Columnas de la grilla"
              data-columnas-grilla
              className="flex items-center gap-1 rounded-full bg-tarjeta p-1 text-[13px] ring-1 ring-borde"
            >
              {/* Con el filtro de feed e historias, en 1360 la palabra no entra (DEC-21). */}
              <span className="hidden pr-1 pl-2 text-tinta-suave min-[1500px]:inline">
                Columnas
              </span>
              {COLUMNAS_POSIBLES.map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={columnas.valor === n}
                  onClick={() => {
                    columnas.alCambiar(n)
                  }}
                  className={cn(
                    'size-7 rounded-full font-medium tabular-nums',
                    columnas.valor === n
                      ? 'bg-tinta text-white'
                      : 'text-tinta-suave hover:bg-lienzo',
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          )}
          {hayMenu && (
            <MenuDesplegable>
              <MenuDisparador asChild>
                <Boton
                  variante="sutil"
                  tamano="icono"
                  aria-label="Más opciones"
                  // Sin filtros (el cliente), el menú sólo trae lo que en
                  // escritorio ya está a la vista.
                  className={cn(!f && 'sm:hidden')}
                >
                  <MoreVerticalIcon className="size-5" aria-hidden />
                </Boton>
              </MenuDisparador>
              <MenuContenido align="end" className="min-w-60">
                {accionesMenu && (
                  <div className="sm:hidden">
                    {accionesMenu}
                    <MenuSeparador className="my-1 h-px bg-borde" />
                  </div>
                )}
                {puedeCompleta && !completa && (
                  <div className="sm:hidden">
                    <MenuItem
                      onSelect={() => {
                        alCambiarCompleta?.(true)
                      }}
                    >
                      <LayoutGridIcon className="size-4" aria-hidden />
                      Ver grilla completa
                    </MenuItem>
                    {f && <MenuSeparador className="my-1 h-px bg-borde" />}
                  </div>
                )}
                {f && f.pilares.length > 0 && (
                  <>
                    <p className="px-3 pt-1.5 pb-1 text-xs font-medium text-tinta-suave">
                      Filtrar por pilar
                    </p>
                    {f.pilares.map((p) => (
                      <MenuItem
                        key={p.id}
                        data-pilar-filtro={p.nombre}
                        onSelect={(e) => {
                          // El menú queda abierto: los filtros se combinan (§8.2).
                          e.preventDefault()
                          alternarPilar(p.id)
                        }}
                      >
                        <CheckIcon
                          className={cn(
                            'size-4',
                            !f.pilarIds.includes(p.id) && 'invisible',
                          )}
                          aria-hidden
                        />
                        <span
                          className="size-2.5 rounded-full"
                          style={{
                            backgroundColor: `var(--color-pilar-${String(p.color)})`,
                          }}
                          aria-hidden
                        />
                        {p.nombre}
                      </MenuItem>
                    ))}
                  </>
                )}
                {f && vista === 'grilla' && (
                  <>
                    {f.pilares.length > 0 && (
                      <MenuSeparador className="my-1 h-px bg-borde" />
                    )}
                    <MenuItem
                      data-mostrar-baja
                      onSelect={() => {
                        f.alCambiarDeBaja(!f.mostrarDeBaja)
                      }}
                    >
                      <CheckIcon
                        className={cn('size-4', !f.mostrarDeBaja && 'invisible')}
                        aria-hidden
                      />
                      Mostrar dadas de baja
                    </MenuItem>
                  </>
                )}
              </MenuContenido>
            </MenuDesplegable>
          )}
        </div>
      </Pagina>

      {conMes && hayChips && (
        <Pagina className="-mt-1 flex flex-wrap items-center gap-2 pb-2.5">
          <span className="text-xs text-tinta-suave">Filtrando:</span>
          {destino !== 'todo' && (
            <Chip
              etiqueta={destino === 'historia' ? 'Sólo historias' : 'Sólo feed'}
              resaltado
              alQuitar={() => {
                filtroDestino?.alCambiar('todo')
              }}
            />
          )}
          {pilaresActivos.map((p) => (
            <Chip
              key={p.id}
              etiqueta={p.nombre}
              color={p.color}
              alQuitar={() => {
                alternarPilar(p.id)
              }}
            />
          ))}
          {f && verBaja && (
            <Chip
              etiqueta="Dadas de baja"
              alQuitar={() => {
                f.alCambiarDeBaja(false)
              }}
            />
          )}
          {cantidadChips > 1 && (
            <button
              type="button"
              className="text-xs text-tinta-suave underline-offset-2 hover:text-tinta hover:underline"
              onClick={() => {
                f?.alQuitarTodos()
                filtroDestino?.alCambiar('todo')
              }}
            >
              Quitar todos
            </button>
          )}
        </Pagina>
      )}
    </div>
  )
}

/** Las tres opciones del filtro de feed e historias (DEC-21). */
const OPCIONES_DESTINO: { valor: FiltroDestino; etiqueta: string }[] = [
  { valor: 'todo', etiqueta: 'Todo' },
  { valor: 'feed', etiqueta: 'Feed' },
  { valor: 'historia', etiqueta: 'Historias' },
]

function Chip({
  etiqueta,
  color,
  resaltado = false,
  alQuitar,
}: {
  etiqueta: string
  color?: number
  /** Con el color de las historias, para que no pase desapercibido (OBS-017). */
  resaltado?: boolean
  alQuitar: () => void
}) {
  return (
    <span
      data-filtro-activo={etiqueta}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full py-1 pr-1 pl-3 text-xs font-medium ring-1',
        resaltado
          ? 'bg-historia/10 text-historia ring-historia/40'
          : 'bg-tarjeta ring-borde',
      )}
    >
      {color !== undefined && (
        <span
          className="size-2 rounded-full"
          style={{ backgroundColor: `var(--color-pilar-${String(color)})` }}
          aria-hidden
        />
      )}
      {etiqueta}
      <button
        type="button"
        aria-label={`Quitar el filtro ${etiqueta}`}
        onClick={alQuitar}
        className="flex size-6 items-center justify-center rounded-full hover:bg-lienzo"
      >
        <XIcon className="size-3.5" aria-hidden />
      </button>
    </span>
  )
}

/**
 * Barra fija de pestañas del celular (Fase 5a): Planificación · Grilla ·
 * Calendario · Recursos, con ícono y nombre. Los botones flotantes "Nueva…"
 * se apoyan encima.
 */
export function BarraVistas({
  vista,
  alCambiarVista,
}: {
  vista: Vista
  alCambiarVista: (v: Vista) => void
}) {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-borde bg-tarjeta pb-[env(safe-area-inset-bottom)] sm:hidden"
      aria-label="Vistas del proyecto"
    >
      <div role="tablist" aria-label="Vista" className="grid grid-cols-4">
        {VISTAS.map((v) => {
          const Icono = v.icono
          const activa = vista === v.valor
          return (
            <button
              key={v.valor}
              type="button"
              role="tab"
              aria-selected={activa}
              onClick={() => {
                alCambiarVista(v.valor)
              }}
              className={cn(
                'flex min-h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium',
                activa ? 'text-tinta' : 'text-tinta-suave',
              )}
            >
              <span
                className={cn(
                  'flex h-7 w-12 items-center justify-center rounded-full',
                  activa && 'bg-tinta text-white',
                )}
              >
                <Icono className="size-4" aria-hidden />
              </span>
              {v.etiqueta}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
